import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { navigate } from 'gatsby';

import {
  Button,
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  formControlLabelClasses,
  Grid2 as Grid,
  styled,
  useTheme,
} from '@mui/material';

import { Brochure, NavigationLinks } from '@mewa/types';
import { CheckmarkFilledIcon, ResponsiveGrid, StageWithCta } from '@mewa/ui';

import t from '../../../../libs/translate';
import Img from '../../../components/image';
import { previousLocationWasExternal } from '../../../helpers/previous-location-state';
import { BackButtonContainer, ButtonContainer } from '../base';
import { loadBrochures, storeBrochures } from '../persistence';
import { ConversionTranslations } from '../translations-query';
import { GridContainer } from './base';
import { BrochureInfo } from './helper';

const BrochuresSelection = ({
  stageImage,
  mobileStageImage,
  onSelect,
  translations,
  availableBrochures,
  navigationLinks,
  langPrefix,
}: {
  stageImage: string;
  mobileStageImage: string;
  onSelect: (brochures: Brochure[]) => void;
  translations: ConversionTranslations;
  availableBrochures: BrochureInfo[];
  navigationLinks: NavigationLinks;
  langPrefix: string;
}) => {
  const theme = useTheme();
  const [selection, setSelection] = useState<Set<Brochure>>(new Set());

  const handleCatalogSelection = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const catalogName = event.target.name as Brochure;
      const checked = event.target.checked;
      setSelection((current) => {
        const newSelection = new Set(current);

        if (checked) {
          newSelection.add(catalogName);
        } else {
          newSelection.delete(catalogName);
        }

        // backup in case of browser reload
        storeBrochures(Array.from(newSelection));

        return newSelection;
      });
    },
    [setSelection],
  );

  useEffect(() => {
    const preselection = loadBrochures();

    if (preselection) {
      setSelection(new Set(preselection));
    }
  }, [setSelection]);

  const image = useMemo(
    () => (
      <Img
        images={[
          {
            src: `${mobileStageImage}`,
            media: `(max-width: ${theme.breakpoints.values.sm - 1}px)`,
            width: 375,
            height: 161,
          },
          {
            src: `${stageImage}`,
            media: `(min-width: ${theme.breakpoints.values.sm}px)`,
            width: 1920,
            height: 568,
          },
        ]}
        style={{
          width: '100%',
        }}
        alt=""
      />
    ),
    [stageImage, mobileStageImage, theme],
  );

  const onBackClick = () => {
    if (previousLocationWasExternal()) {
      navigate(`/${langPrefix}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      <StageWithCta
        title={navigationLinks.conversion.brochuresDownload.name}
        image={image}
      />
      <ResponsiveGrid sx={{ marginTop: 'var(--spacer-xl)' }}>
        <Grid size={12}>
          <Intro>{t(translations, 'brochures_download_form_intro')}</Intro>
          <Description>
            {t(translations, 'brochures_download_form_description')}
          </Description>
          <GridContainer>
            {availableBrochures
              .filter((brochure) => brochure.fileCount > 0)
              .map((brochure) => {
                const checked = selection.has(brochure.key);

                return (
                  <BrochureCheckbox
                    key={brochure.key}
                    brochure={brochure}
                    checked={checked}
                    onChange={handleCatalogSelection}
                  ></BrochureCheckbox>
                );
              })}
          </GridContainer>
          <ButtonContainer>
            <BackButtonContainer>
              <Button
                onClick={onBackClick}
                variant="outlined"
                color="primary"
                sx={{ width: '100%' }}
              >
                {t(translations, 'general_form_button_label_back')}
              </Button>
            </BackButtonContainer>
            <Button
              onClick={() => onSelect(Array.from(selection))}
              disabled={selection.size === 0}
              variant="contained"
              color="primary"
              sx={{ flexBasis: { xs: '100%', sm: 'auto' } }}
            >
              {t(translations, 'general_form_button_label_next')}
            </Button>
          </ButtonContainer>
        </Grid>
      </ResponsiveGrid>
    </>
  );
};

const BrochureCheckbox = React.memo(
  ({
    checked,
    brochure: { key, fileCount, name, imageSrc },
    onChange,
  }: {
    checked: boolean;
    brochure: BrochureInfo;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  }) => {
    const displayName = name;
    const imageData = useMemo(
      () => [
        {
          src: imageSrc,
          width: 640,
          height: 840,
          media: '',
        },
      ],
      [imageSrc],
    );

    return (
      <CheckboxContainer
        className={`link-container ${checked ? 'checked' : ''}`}
      >
        <CustomCheckbox
          control={
            <Checkbox
              name={key}
              checked={checked}
              onChange={onChange}
              style={{ height: 0, width: 0 }}
            />
          }
          label={
            <div
              style={{
                display: 'grid',
                gridTemplateRows: 'auto auto auto 1fr',
                height: '100%',
              }}
            >
              <Img images={imageData} alt=""></Img>
              <Title>{displayName}</Title>
              <FileInfo>
                {fileCount} PDF{fileCount === 1 ? '' : 's'}
              </FileInfo>
              <IconContainer>
                {checked ? <CheckmarkFilledIcon /> : null}
              </IconContainer>
            </div>
          }
        />
      </CheckboxContainer>
    );
  },
);

const CustomCheckbox = styled(FormControlLabel)(({ theme }) => ({
  height: 'auto',

  [`.${checkboxClasses.root}`]: { display: 'none' },
  [`&.${formControlLabelClasses.root}`]: {
    margin: 0,
    padding: 0,
    display: 'block',
    height: '100%',
  },

  [`.${formControlLabelClasses.label}`]: {
    display: 'block',
    width: '100%',
    height: '100%',
  },
}));

const CheckboxContainer = styled('div')(({ theme }) => ({
  '.gatsby-image-wrapper, img': {
    aspectRatio: '320 / 420',
  },
  padding: '5px',
  margin: '-5px',

  [theme.breakpoints.up('sm')]: {
    padding: '8px',
    margin: '-8px',

    '&:hover': {
      boxShadow: '0px 0px 24px 4px rgba(211, 211, 211)',
    },
  },

  '&.checked': {
    boxShadow: '0px 0px 24px 4px rgba(211, 211, 211)',
  },

  [theme.breakpoints.up('md')]: {
    padding: '8px',
    margin: '-8px',
  },

  [theme.breakpoints.up('lg')]: {
    padding: '10px',
    margin: '-10px',
  },
}));

const Title = styled('h3')(({ theme }) => ({
  ...theme.typography.h6,
  margin: '10px 0 5px',
}));

const FileInfo = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  margin: '0 0 var(--spacer-xxs)',
}));

const IconContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '48px',
  justifyContent: 'flex-end',
  alignSelf: 'flex-end',

  svg: {
    height: '48px',
    width: '48px',
  },
}));

const Intro = styled('h2')(({ theme }) => ({
  ...theme.typography.h2,
  fontWeight: theme.typography.fontWeightMedium,
  margin: 0,

  [theme.breakpoints.up('sm')]: {
    width: '50%',
  },
}));

const Description = styled('p')(({ theme }) => ({
  ...theme.typography.h6,
  fontWeight: theme.typography.fontWeightRegular,
  margin: 'var(--spacer-l) 0 0',

  [theme.breakpoints.up('sm')]: {
    width: '50%',
  },
}));

export default BrochuresSelection;
